import { useCallback } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _includes from 'lodash/includes';
import _uniqBy from 'lodash/uniqBy';
import {
  gqlGenderAppearanceEnum as GENDER_APPEARANCE,
  gqlGenderIdentityEnum as GENDER_IDENTITY,
} from '@minecraft.graphql-types';
import { FilterValues, GetGenderAppearanceArgs } from '../types';
import { useGenderOptions } from './useGenderOptions';
import { useGenderIdentityOptions } from './useGenderIdentityOptions';

export const useGenderAppearance = () => {
  const genderOptions = useGenderOptions();
  const genderIdentityOptions = useGenderIdentityOptions();

  return useCallback(
    ({ genderName, additionallyIdentifiesAsName, roleDetails }: GetGenderAppearanceArgs): FilterValues => {
      const result = {
        [genderName]: [],
        [additionallyIdentifiesAsName]: [],
      };

      if (_isEmpty(roleDetails.genderAppearance)) return [];

      // When all kinds of gender representations are selected, the filter should contain all values.
      if (roleDetails.genderAppearance?.length === Object.keys(GENDER_APPEARANCE).length) {
        return genderOptions;
      }

      const roleGenderAppearanceCodes = _map(
        roleDetails.genderAppearance,
        (roleGenderAppearance) => roleGenderAppearance.code
      );
      const isManGenderAvailable = _includes(roleGenderAppearanceCodes, GENDER_APPEARANCE.MAN);
      const isWomanGenderAvailable = _includes(roleGenderAppearanceCodes, GENDER_APPEARANCE.WOMAN);
      const isNonBinaryPersonGenderAvailable = _includes(
        roleGenderAppearanceCodes,
        GENDER_APPEARANCE.NON_BINARY_PERSON
      );
      const isTransManGenderAvailable = _includes(roleGenderAppearanceCodes, GENDER_APPEARANCE.TRANS_MAN);
      const isTransWomanGenderAvailable = _includes(roleGenderAppearanceCodes, GENDER_APPEARANCE.TRANS_WOMAN);

      if (isManGenderAvailable) {
        result[genderName].push({
          fieldName: genderName,
          value: GENDER_APPEARANCE.MAN,
          label: _find(genderOptions, { value: GENDER_APPEARANCE.MAN }).label,
        });
      }

      if (isWomanGenderAvailable) {
        result[genderName].push({
          fieldName: genderName,
          value: GENDER_APPEARANCE.WOMAN,
          label: _find(genderOptions, { value: GENDER_APPEARANCE.WOMAN }).label,
        });
      }

      if (isNonBinaryPersonGenderAvailable) {
        result[genderName].push({
          fieldName: genderName,
          value: GENDER_APPEARANCE.NON_BINARY_PERSON,
          label: _find(genderOptions, { value: GENDER_APPEARANCE.NON_BINARY_PERSON }).label,
        });
      }

      if (isTransManGenderAvailable) {
        result[genderName].push({
          fieldName: genderName,
          value: GENDER_APPEARANCE.MAN,
          label: _find(genderOptions, { value: GENDER_APPEARANCE.MAN }).label,
        });
        result[additionallyIdentifiesAsName].push({
          fieldName: additionallyIdentifiesAsName,
          value: GENDER_IDENTITY.TRANS,
          label: _find(genderIdentityOptions, { value: GENDER_IDENTITY.TRANS }).label,
        });
        result[additionallyIdentifiesAsName].push({
          fieldName: additionallyIdentifiesAsName,
          value: GENDER_IDENTITY.NON_BINARY,
          label: _find(genderIdentityOptions, { value: GENDER_IDENTITY.NON_BINARY }).label,
        });
      }

      if (isTransWomanGenderAvailable) {
        result[genderName].push({
          fieldName: genderName,
          value: GENDER_APPEARANCE.WOMAN,
          label: _find(genderOptions, { value: GENDER_APPEARANCE.WOMAN }).label,
        });
        result[additionallyIdentifiesAsName].push({
          fieldName: additionallyIdentifiesAsName,
          value: GENDER_IDENTITY.TRANS,
          label: _find(genderIdentityOptions, { value: GENDER_IDENTITY.TRANS }).label,
        });
        result[additionallyIdentifiesAsName].push({
          fieldName: additionallyIdentifiesAsName,
          value: GENDER_IDENTITY.NON_BINARY,
          label: _find(genderIdentityOptions, { value: GENDER_IDENTITY.NON_BINARY }).label,
        });
      }

      return _uniqBy([...result[genderName], ...result[additionallyIdentifiesAsName]], 'value');
    },
    [genderIdentityOptions, genderOptions]
  );
};
